import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import api from "../lib/api";
import { formatApiError } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { QrCode, Banknote, Tag, Truck } from "lucide-react";

const METHODS = [
    { id: "qr_code", label: "QR-code (SEPA)", desc: "Scan met je bank-app (ING, KBC, BNP, Belfius, Argenta, …). Bedrag & mededeling worden automatisch ingevuld." },
    { id: "bank_transfer", label: "Bankoverschrijving", desc: "IBAN: BE16 0004 1120 9874 — VdS Gift Shop" },
];

const SHIPPING = {
    "België": 6.95,
    "Nederland": 11.75,
};

export default function CheckoutPage() {
    const { user } = useAuth();
    const { cart, total, count, fetchCart, addToCart } = useCart();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        full_name: user?.name || "", address: "", city: "",
        postal_code: "", country: "België", phone: "", notes: "",
    });
    const [method, setMethod] = useState("qr_code");
    const [submitting, setSubmitting] = useState(false);
    const [qrPreview, setQrPreview] = useState(null);
    const [promoConfig, setPromoConfig] = useState({ volume_tiers: [], free_shipping_threshold: 75 });
    const [codeInput, setCodeInput] = useState("");
    const [appliedCode, setAppliedCode] = useState(null);   // {code, amount, free_shipping, ...}
    const [validatingCode, setValidatingCode] = useState(false);
    const [upsellItems, setUpsellItems] = useState([]);
    const [addingUpsell, setAddingUpsell] = useState(null);
    const [galleryConsent, setGalleryConsent] = useState(false);
    const [galleryDisplayName, setGalleryDisplayName] = useState(user?.name || "");
    // ----- Gift options (Fase 1) -----
    const [giftWrap, setGiftWrap] = useState(false);   // legacy compat
    const [giftWrapTier, setGiftWrapTier] = useState("none"); // "none" | "gift" | "luxe"
    const [sendAsGift, setSendAsGift] = useState(false);
    const [giftRecipient, setGiftRecipient] = useState({ name: "", address: "", city: "", postal_code: "", country: "België" });
    const [giftMessage, setGiftMessage] = useState("");
    const [giftCardCode, setGiftCardCode] = useState("");
    const [giftCardApplied, setGiftCardApplied] = useState(null);   // {code, balance}
    const [checkingGiftCard, setCheckingGiftCard] = useState(false);
    const GIFT_WRAP_FEES = { none: 0, gift: 3.50, luxe: 6.95 };
    const GIFT_WRAP_FEE = GIFT_WRAP_FEES[giftWrapTier] || 0;

    useEffect(() => {
        api.get("/promo/config").then(({ data }) => setPromoConfig(data)).catch(() => {});
        api.get("/templates").then(({ data }) => {
            // Show max 3 random templates that aren't already in cart
            const inCart = new Set((cart.items || []).map(i => i.template_id).filter(Boolean));
            const filtered = data.filter(t => !inCart.has(t.id));
            setUpsellItems(filtered.sort(() => Math.random() - 0.5).slice(0, 3));
        }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addUpsell = async (tpl) => {
        const size = tpl.available_sizes?.[0];
        if (!size) return;
        setAddingUpsell(tpl.id);
        try {
            await addToCart({ template_id: tpl.id, size }, "template");
            toast.success(`${tpl.name} (${size}) toegevoegd!`);
            setUpsellItems(prev => prev.filter(x => x.id !== tpl.id));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally {
            setAddingUpsell(null);
        }
    };

    // Compute volume discount automatically from cart quantity
    const volumeDiscount = useMemo(() => {
        const tiers = (promoConfig.volume_tiers || []).slice().sort((a, b) => b.min_qty - a.min_qty);
        const tier = tiers.find(t => count >= t.min_qty);
        if (!tier) return { percent: 0, amount: 0, label: null };
        const amount = Math.round(total * (tier.percent / 100) * 100) / 100;
        return { percent: tier.percent, amount, label: `Volume korting ${tier.percent}% (${count}+ items)` };
    }, [count, total, promoConfig]);

    // Workwear-specifieke staffelkorting (alleen op werkkledij-regels)
    const workwearDiscount = useMemo(() => {
        const items = (cart?.items || []).filter(i => i.item_type === "workwear");
        const wwQty = items.reduce((s, i) => s + (i.quantity || 0), 0);
        const wwSubtotal = items.reduce((s, i) => s + (i.line_total || 0), 0);
        const tiers = (promoConfig.workwear_volume_tiers || []).slice().sort((a, b) => b.min_qty - a.min_qty);
        const tier = tiers.find(t => wwQty >= t.min_qty);
        if (!tier || wwSubtotal <= 0) return { percent: 0, amount: 0, label: null, wwQty, wwSubtotal };
        const amount = Math.round(wwSubtotal * (tier.percent / 100) * 100) / 100;
        return { percent: tier.percent, amount, label: `Werkkledij staffel ${tier.percent}% (${wwQty}+ stuks)`, wwQty, wwSubtotal };
    }, [cart, promoConfig]);

    const codeDiscount = appliedCode?.amount || 0;
    const subtotalAfterDiscounts = Math.max(0, total - volumeDiscount.amount - workwearDiscount.amount - codeDiscount);
    const baseShipping = SHIPPING[form.country] || 11.75;
    const freeShipThreshold = promoConfig.free_shipping_threshold || 75;
    const qualifiesFreeShipping = appliedCode?.free_shipping || subtotalAfterDiscounts >= freeShipThreshold;
    const shippingCost = qualifiesFreeShipping ? 0 : baseShipping;
    const giftWrapFee = GIFT_WRAP_FEE;
    const grossBeforeGiftCard = Math.max(0, subtotalAfterDiscounts + shippingCost + giftWrapFee);
    const giftCardCredit = giftCardApplied ? Math.min(giftCardApplied.balance, grossBeforeGiftCard) : 0;
    const grandTotal = Math.max(0, grossBeforeGiftCard - giftCardCredit);
    const amountToFreeShipping = Math.max(0, freeShipThreshold - subtotalAfterDiscounts);
    const freeShipProgress = Math.min(100, (subtotalAfterDiscounts / freeShipThreshold) * 100);

    // Fetch live preview QR-code based on running grand total
    useEffect(() => {
        if (method !== "qr_code" || grandTotal <= 0) { setQrPreview(null); return; }
        let cancelled = false;
        const ref = `VdS-${(form.full_name || "Cart").replace(/[^A-Za-z0-9]/g, "").slice(0, 10) || "Cart"}`;
        api.get(`/payment-qr/preview?amount=${grandTotal.toFixed(2)}&reference=${encodeURIComponent(ref)}`)
            .then(({ data }) => { if (!cancelled) setQrPreview(data); })
            .catch(() => { if (!cancelled) setQrPreview(null); });
        return () => { cancelled = true; };
    }, [method, grandTotal, form.full_name]);

    if (!user) return <div className="p-10"><Link to="/login" className="btn-primary">Login</Link></div>;
    if (!cart.items?.length) {
        return <div className="p-10 text-center">
            <p className="text-zinc-500">Je mandje is leeg.</p>
            <Link to="/designer" className="btn-primary mt-4 inline-block">Start ontwerp</Link>
        </div>;
    }

    const change = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

    const applyCode = async () => {
        if (!codeInput.trim()) return;
        setValidatingCode(true);
        try {
            const { data } = await api.post("/discount/apply", { code: codeInput, subtotal: total, item_count: count });
            setAppliedCode(data);
            toast.success(`Code "${data.code}" toegepast — €${data.amount.toFixed(2)} korting!`);
            setCodeInput("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Ongeldige code");
            setAppliedCode(null);
        } finally {
            setValidatingCode(false);
        }
    };

    const removeCode = () => { setAppliedCode(null); toast.success("Code verwijderd"); };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/checkout", {
                ...form,
                payment_method: method,
                discount_code: appliedCode?.code || null,
                discount_amount: codeDiscount,
                volume_discount_amount: volumeDiscount.amount,
                workwear_discount_amount: workwearDiscount.amount,
                free_shipping_applied: qualifiesFreeShipping,
                gallery_consent: galleryConsent,
                gallery_display_name: galleryConsent ? (galleryDisplayName || "").trim() : null,
                gift_wrap: giftWrapTier !== "none",
                gift_wrap_tier: giftWrapTier,
                send_as_gift: sendAsGift,
                gift_recipient_name: sendAsGift ? giftRecipient.name : null,
                gift_recipient_address: sendAsGift ? giftRecipient.address : null,
                gift_recipient_city: sendAsGift ? giftRecipient.city : null,
                gift_recipient_postal_code: sendAsGift ? giftRecipient.postal_code : null,
                gift_recipient_country: sendAsGift ? giftRecipient.country : null,
                gift_message: giftMessage || null,
                gift_card_code: giftCardApplied?.code || null,
                gift_card_amount: giftCardApplied?.balance || 0,
            });
            await fetchCart();
            toast.success("Bestelling geplaatst!");
            navigate(`/order/${data.id}`, { state: { order: data } });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const checkGiftCard = async () => {
        if (!giftCardCode.trim()) return;
        setCheckingGiftCard(true);
        try {
            const { data } = await api.post("/gift-cards/check", { code: giftCardCode.trim() });
            setGiftCardApplied({ code: data.code, balance: data.balance });
            toast.success(`Cadeaubon geldig: € ${data.balance.toFixed(2)} saldo`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Ongeldige code");
            setGiftCardApplied(null);
        } finally {
            setCheckingGiftCard(false);
        }
    };
    const removeGiftCard = () => { setGiftCardApplied(null); setGiftCardCode(""); };

    return (
        <div data-testid="checkout-page" className="bg-zinc-50 min-h-screen">
            <Toaster />
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
                <form onSubmit={submit} className="lg:col-span-8 space-y-8">
                    <div>
                        <p className="label-tiny">Checkout</p>
                        <h1 className="font-display text-5xl mt-2">Bezorging.</h1>
                    </div>
                    <div className="card p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Volledige naam" testId="checkout-name"><input className="input-flat" required value={form.full_name} onChange={change("full_name")} data-testid="checkout-name-input" /></Field>
                        <Field label="Telefoon" testId="checkout-phone"><input className="input-flat" required value={form.phone} onChange={change("phone")} data-testid="checkout-phone-input" /></Field>
                        <Field label="Adres" testId="checkout-address" wide><input className="input-flat" required value={form.address} onChange={change("address")} data-testid="checkout-address-input" /></Field>
                        <Field label="Stad" testId="checkout-city"><input className="input-flat" required value={form.city} onChange={change("city")} data-testid="checkout-city-input" /></Field>
                        <Field label="Postcode" testId="checkout-postal"><input className="input-flat" required value={form.postal_code} onChange={change("postal_code")} data-testid="checkout-postal-input" /></Field>
                        <Field label="Land" testId="checkout-country" wide>
                            <select
                                data-testid="checkout-country-input"
                                className="input-flat"
                                value={form.country}
                                onChange={change("country")}
                            >
                                <option value="België">België — bpost €6,95</option>
                                <option value="Nederland">Nederland — bpost €11,75</option>
                            </select>
                        </Field>
                        <Field label="Opmerkingen (optioneel)" testId="checkout-notes" wide><textarea rows={3} className="input-flat" value={form.notes} onChange={change("notes")} data-testid="checkout-notes-input" /></Field>
                    </div>

                    <div>
                        <p className="label-tiny mb-3">Betaalmethode</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-zinc-200">
                            {METHODS.map(m => (
                                <button
                                    type="button"
                                    data-testid={`payment-${m.id}`}
                                    key={m.id}
                                    onClick={() => setMethod(m.id)}
                                    className={`text-left p-5 border-r border-b border-zinc-200 transition ${method === m.id ? "bg-zinc-950 text-white" : "bg-white"}`}>
                                    <div className="flex items-center gap-2">
                                        {m.id === "qr_code" ? <QrCode size={16} /> : <Banknote size={16} />}
                                        <p className="font-semibold">{m.label}</p>
                                    </div>
                                    <p className={`text-xs mt-2 ${method === m.id ? "text-zinc-300" : "text-zinc-500"}`}>{m.desc}</p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {method === "qr_code" && qrPreview && (
                        <div data-testid="checkout-qr-preview" className="border border-zinc-200 bg-white p-6 flex flex-col md:flex-row items-center gap-6">
                            <img src={qrPreview.data_url} alt="SEPA QR Preview" className="w-44 h-44 border border-zinc-100" data-testid="checkout-qr-image" />
                            <div className="flex-1 space-y-2 text-sm">
                                <p className="label-tiny">Voorbeeld QR — Scan om te betalen</p>
                                <p className="text-zinc-700">Open je bank-app → &quot;Betalen&quot; → Scan QR. Bedrag <strong>€ {grandTotal.toFixed(2)}</strong> en de mededeling worden automatisch ingevuld.</p>
                                <p className="text-xs text-zinc-500 font-mono-dm">IBAN: {qrPreview.iban.replace(/(.{4})/g, "$1 ").trim()} · {qrPreview.beneficiary}</p>
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">⚠ Bevestig eerst je bestelling hieronder — je krijgt dan een definitieve QR met je bestelnummer als mededeling.</p>
                            </div>
                        </div>
                    )}

                    {upsellItems.length > 0 && (
                        <div data-testid="checkout-upsell" className="border border-amber-200 bg-amber-50 p-5">
                            <p className="label-tiny text-amber-900 mb-3">✨ Misschien ook leuk?</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {upsellItems.map(t => (
                                    <div key={t.id} data-testid={`upsell-${t.id}`} className="bg-white border border-zinc-200 p-3 flex gap-3 items-center">
                                        <div className="w-16 h-16 bg-zinc-50 border border-zinc-100 flex-shrink-0 overflow-hidden">
                                            <img src={t.preview_image_url} alt={t.name} className="w-full h-full object-contain" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-xs truncate">{t.name}</p>
                                            <p className="font-mono-dm text-xs">€ {t.price.toFixed(2)}</p>
                                            <button
                                                type="button"
                                                onClick={() => addUpsell(t)}
                                                disabled={addingUpsell === t.id}
                                                data-testid={`upsell-add-${t.id}`}
                                                className="mt-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 hover:underline"
                                            >{addingUpsell === t.id ? "Bezig…" : "+ Toevoegen"}</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div data-testid="gallery-consent-block" className="border border-zinc-200 bg-white p-5">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="gallery-consent-checkbox"
                                checked={galleryConsent}
                                onChange={(e) => setGalleryConsent(e.target.checked)}
                                className="mt-1 w-4 h-4 accent-rose-600"
                            />
                            <div className="flex-1">
                                <p className="font-semibold text-sm">📸 Deel mijn ontwerp in de klantengalerij</p>
                                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                                    Andere klanten zien jouw ontwerp ter inspiratie. We tonen enkel je <strong>weergavenaam</strong>{" "}
                                    en het ontwerp — nooit je e-mailadres of adres. Elk item wordt eerst goedgekeurd door ons team.
                                </p>
                            </div>
                        </label>
                        {galleryConsent && (
                            <div className="mt-4 pl-7 fade-in">
                                <label className="label-tiny block mb-2">Weergavenaam in galerij</label>
                                <input
                                    data-testid="gallery-display-name-input"
                                    type="text"
                                    value={galleryDisplayName}
                                    onChange={(e) => setGalleryDisplayName(e.target.value)}
                                    placeholder="bv. Jonas D. of Anoniem"
                                    maxLength={40}
                                    className="input-flat max-w-sm"
                                />
                                <p className="text-[10px] text-zinc-400 mt-1">Max. 40 tekens — kies bv. een voornaam of bijnaam.</p>
                            </div>
                        )}
                    </div>

                    {/* Gift options (Fase 1) */}
                    <div data-testid="gift-options-block" className="border border-rose-200 bg-rose-50/50 p-5 space-y-4">
                        <p className="label-tiny text-rose-600">🎁 Cadeau opties</p>

                        {/* 3-tier verpakkings-selector */}
                        <div>
                            <p className="text-sm font-semibold mb-2">Cadeauverpakking</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                {[
                                    { id: "none", label: "Standaard", desc: "Eco kraft + sticker", fee: 0, icon: "📦" },
                                    { id: "gift", label: "Cadeau", desc: "Geschenkdoos + lint + kaartje", fee: 3.50, icon: "🎀" },
                                    { id: "luxe", label: "Luxe gift box", desc: "Magneetdoos + zijdepapier + premium kaartje", fee: 6.95, icon: "💎" },
                                ].map(tier => (
                                    <button key={tier.id} type="button"
                                        data-testid={`giftwrap-${tier.id}`}
                                        onClick={() => setGiftWrapTier(tier.id)}
                                        className={`border-2 p-3 text-left transition-all ${giftWrapTier === tier.id ? "border-rose-600 bg-white shadow-sm" : "border-zinc-200 bg-white/60 hover:border-rose-400"}`}>
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="font-bold text-sm">{tier.icon} {tier.label}</span>
                                            <span className="font-mono-dm text-xs">{tier.fee === 0 ? "GRATIS" : `+€${tier.fee.toFixed(2)}`}</span>
                                        </div>
                                        <p className="text-[10px] text-zinc-500 mt-1">{tier.desc}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-rose-200">
                            <input
                                type="checkbox"
                                data-testid="checkout-send-as-gift"
                                checked={sendAsGift}
                                onChange={(e) => setSendAsGift(e.target.checked)}
                                className="mt-1 w-4 h-4 accent-rose-600"
                            />
                            <div className="flex-1">
                                <p className="font-semibold text-sm">Stuur direct als cadeau naar iemand anders</p>
                                <p className="text-xs text-zinc-500 mt-0.5">Andere afleveradres + persoonlijke boodschap meegestuurd.</p>
                            </div>
                        </label>

                        {/* Standalone gratis kaartje (ook zonder send_as_gift) */}
                        {!sendAsGift && (
                            <div className="pl-7 pt-2" data-testid="standalone-gift-message">
                                <label className="label-tiny mb-1 block">
                                    Voeg gratis een handgeschreven kaartje toe (optioneel) — {giftMessage.length}/280
                                </label>
                                <textarea
                                    data-testid="standalone-gift-message-input"
                                    value={giftMessage}
                                    onChange={(e) => setGiftMessage(e.target.value)}
                                    rows={2}
                                    maxLength={280}
                                    placeholder="Bv. 'Voor de liefste mama — Veel liefs van Lotte 💕'"
                                    className="input-flat resize-none text-sm"
                                />
                            </div>
                        )}

                        {sendAsGift && (
                            <div data-testid="gift-recipient-block" className="pl-7 grid grid-cols-1 md:grid-cols-2 gap-3 fade-in">
                                <Field label="Naam ontvanger" testId="gift-recipient-name">
                                    <input className="input-flat" required value={giftRecipient.name}
                                        onChange={(e) => setGiftRecipient(g => ({ ...g, name: e.target.value }))}
                                        data-testid="gift-recipient-name-input" />
                                </Field>
                                <Field label="Adres" testId="gift-recipient-address">
                                    <input className="input-flat" required value={giftRecipient.address}
                                        onChange={(e) => setGiftRecipient(g => ({ ...g, address: e.target.value }))}
                                        data-testid="gift-recipient-address-input" />
                                </Field>
                                <Field label="Stad" testId="gift-recipient-city">
                                    <input className="input-flat" required value={giftRecipient.city}
                                        onChange={(e) => setGiftRecipient(g => ({ ...g, city: e.target.value }))}
                                        data-testid="gift-recipient-city-input" />
                                </Field>
                                <Field label="Postcode" testId="gift-recipient-postal">
                                    <input className="input-flat" required value={giftRecipient.postal_code}
                                        onChange={(e) => setGiftRecipient(g => ({ ...g, postal_code: e.target.value }))}
                                        data-testid="gift-recipient-postal-input" />
                                </Field>
                                <Field label="Land" testId="gift-recipient-country">
                                    <select className="input-flat" value={giftRecipient.country}
                                        onChange={(e) => setGiftRecipient(g => ({ ...g, country: e.target.value }))}
                                        data-testid="gift-recipient-country-select">
                                        <option>België</option>
                                        <option>Nederland</option>
                                    </select>
                                </Field>
                                <Field label={`Persoonlijke boodschap (max 280) — ${giftMessage.length}/280`} testId="gift-message" wide>
                                    <textarea
                                        data-testid="gift-message-input"
                                        value={giftMessage}
                                        onChange={(e) => setGiftMessage(e.target.value)}
                                        rows={2}
                                        maxLength={280}
                                        placeholder="Veel plezier ermee! 🎉"
                                        className="input-flat resize-none"
                                    />
                                </Field>
                            </div>
                        )}

                        {/* Gift card redemption */}
                        <div className="border-t border-rose-200 pt-4">
                            <p className="label-tiny mb-2">Cadeaubon code inwisselen</p>
                            {giftCardApplied ? (
                                <div data-testid="gift-card-applied" className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 px-3 py-2">
                                    <p className="text-xs text-emerald-800">
                                        Code <strong>{giftCardApplied.code}</strong> · saldo € {giftCardApplied.balance.toFixed(2)}
                                    </p>
                                    <button type="button" onClick={removeGiftCard} className="text-[10px] text-rose-600 font-bold uppercase tracking-widest">
                                        Verwijder
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-stretch gap-2">
                                    <input
                                        data-testid="gift-card-code-input"
                                        type="text"
                                        value={giftCardCode}
                                        onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                                        placeholder="XXXX-XXXX-XXXX-XXXX"
                                        className="flex-1 px-3 py-2 border border-zinc-300 font-mono-dm text-sm focus:outline-none focus:border-zinc-950"
                                    />
                                    <button
                                        type="button"
                                        data-testid="gift-card-check-btn"
                                        onClick={checkGiftCard}
                                        disabled={checkingGiftCard || !giftCardCode.trim()}
                                        className="px-4 text-[10px] bg-zinc-950 text-white font-bold uppercase tracking-widest disabled:opacity-60">
                                        {checkingGiftCard ? "…" : "Controleer"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <button type="submit" disabled={submitting} data-testid="checkout-submit-button" className="btn-primary">
                        {submitting ? "Bezig…" : `Bestelling bevestigen — € ${grandTotal.toFixed(2)}`}
                    </button>
                </form>

                <aside className="lg:col-span-4">
                    <div className="border border-zinc-200 bg-white p-6 sticky top-24 space-y-4">
                        <p className="label-tiny">Jouw bestelling</p>
                        <div className="space-y-2 max-h-56 overflow-y-auto">
                            {cart.items.map(it => (
                                <div key={it.id} className="flex justify-between text-xs border-b border-zinc-100 pb-2">
                                    <span>{it.tshirt_name || it.sticker_name || it.template_name} · {it.tshirt_color_name || it.sticker_size_label || ""} · {it.size || ""} × {it.quantity}</span>
                                    <span className="font-mono-dm">€ {it.line_total.toFixed(2)}</span>
                                </div>
                            ))}
                        </div>

                        {/* Gratis-verzending voortgangsbalk */}
                        {!qualifiesFreeShipping && freeShipThreshold > 0 && (
                            <div data-testid="free-shipping-progress" className="border border-emerald-200 bg-emerald-50 p-3 text-xs">
                                <p className="text-emerald-900 flex items-center gap-1.5 font-semibold">
                                    <Truck size={13} /> Nog € {amountToFreeShipping.toFixed(2)} weg van gratis verzending!
                                </p>
                                <div className="mt-2 h-1.5 bg-emerald-100 overflow-hidden">
                                    <div className="h-full bg-emerald-600" style={{ width: `${freeShipProgress}%` }} />
                                </div>
                            </div>
                        )}
                        {qualifiesFreeShipping && (
                            <div data-testid="free-shipping-applied" className="border border-emerald-200 bg-emerald-50 p-3 text-xs flex items-center gap-2 text-emerald-900">
                                <Truck size={14} /> <span>🎉 Gratis verzending toegepast!</span>
                            </div>
                        )}

                        {/* Kortingscode input */}
                        {!appliedCode ? (
                            <div className="space-y-2">
                                <p className="label-tiny flex items-center gap-1"><Tag size={11} /> Kortingscode</p>
                                <div className="flex gap-1.5">
                                    <input
                                        data-testid="checkout-promo-input"
                                        value={codeInput}
                                        onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                                        placeholder="bv. ZOMER15"
                                        className="input-flat flex-1 font-mono-dm text-sm uppercase"
                                    />
                                    <button type="button" data-testid="checkout-promo-apply" onClick={applyCode} disabled={validatingCode || !codeInput.trim()}
                                        className="btn-ghost text-xs">{validatingCode ? "…" : "Toepassen"}</button>
                                </div>
                            </div>
                        ) : (
                            <div data-testid="checkout-promo-applied" className="border border-emerald-300 bg-emerald-50 p-3 text-xs flex items-center justify-between">
                                <span className="text-emerald-900"><Tag size={11} className="inline -mt-0.5 mr-1" /> Code <strong>{appliedCode.code}</strong> · −€ {appliedCode.amount.toFixed(2)}</span>
                                <button type="button" onClick={removeCode} className="text-rose-700 hover:underline" data-testid="checkout-promo-remove">Verwijder</button>
                            </div>
                        )}

                        {/* Totalen */}
                        <div className="border-t border-zinc-200 pt-4 space-y-1.5">
                            <div className="flex justify-between text-sm">
                                <span className="text-zinc-600">Subtotaal ({count} item{count !== 1 ? "s" : ""})</span>
                                <span className="font-mono-dm" data-testid="checkout-subtotal">€ {total.toFixed(2)}</span>
                            </div>
                            {volumeDiscount.amount > 0 && (
                                <div className="flex justify-between text-sm text-emerald-700" data-testid="checkout-volume-discount">
                                    <span>{volumeDiscount.label}</span>
                                    <span className="font-mono-dm">−€ {volumeDiscount.amount.toFixed(2)}</span>
                                </div>
                            )}
                            {workwearDiscount.amount > 0 && (
                                <div className="flex justify-between text-sm text-orange-700" data-testid="checkout-workwear-discount">
                                    <span>{workwearDiscount.label}</span>
                                    <span className="font-mono-dm">−€ {workwearDiscount.amount.toFixed(2)}</span>
                                </div>
                            )}
                            {codeDiscount > 0 && (
                                <div className="flex justify-between text-sm text-emerald-700">
                                    <span>Code {appliedCode.code}</span>
                                    <span className="font-mono-dm">−€ {codeDiscount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm">
                                <span className="text-zinc-600">Verzending {form.country}{qualifiesFreeShipping ? " — gratis" : ""}</span>
                                <span className="font-mono-dm" data-testid="checkout-shipping-cost">€ {shippingCost.toFixed(2)}</span>
                            </div>
                            <div className="border-t border-zinc-200 pt-3 mt-2 flex justify-between items-baseline">
                                <span className="font-semibold">Totaal</span>
                                <span className="font-display text-2xl" data-testid="checkout-total">€ {grandTotal.toFixed(2)}</span>
                            </div>
                            <p className="text-xs text-zinc-500 mt-2">Levering 3-5 werkdagen na betaling.</p>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function Field({ label, children, wide, testId }) {
    return (
        <div className={wide ? "md:col-span-2" : ""}>
            <label data-testid={`${testId}-label`} className="label-tiny block mb-2">{label}</label>
            {children}
        </div>
    );
}
